import {
  ExpensesCustomizationConfigResourceTypeEnum,
  ExpensesValueObjectsCustomizationDocumentTypeEnum,
} from 'generated/resources/graphql'
import { RefinementCtx, z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { hasAnyEnabledField } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import {
  CustomizationField,
  Tax,
  fieldType,
  isMandatory,
} from 'modules/expenses/hooks/useExpensesCustomization'

export interface FormContext {
  expensableAmount?: number
  customizationFields: CustomizationField[]
}

const defaultInvalidNumberMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.invalid_number'),
})

const defaultInvalidPercentageMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.invalid_percentage'),
})

const defaultInvalidPositiveMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.invalid_positive_number'),
})

export interface SuperRefineContext {
  data: {
    amount?: number
    taxes?: Tax[]
    // Add other fields that might be accessed in the validation
    [key: string]: unknown
  }
  ctx: RefinementCtx
  customizationFields: CustomizationField[]
}

export const InitialTaxSchema = () => {
  return z.object({
    taxes: z.array(
      z.object({
        id: z.string(),
        base_amount: z.number().nullish(),
        percentage: z.number().nullish(),
        amount: z.number().nullish(),
      })
    ),
  })
}

const SingleTaxSchema = ({ expensableAmount, customizationFields }: FormContext) => {
  return z.object({
    taxes: z.array(
      z.object({
        id: z.string().nullable(),
        base_amount: z.number().nullable(),
        percentage: z.number().nullable(),
        amount: z
          .number()
          .nullable()
          .refine((value) => {
            return isMandatory('taxes', 'expense', customizationFields) ? !!value : true
          }, i18n.t('expenses_v2.forms.errors.required'))
          .refine(
            (value) => value && value > 0,
            i18n.t('expenses_v2.forms.errors.invalid_positive_number')
          )
          .refine(
            (value) => value && expensableAmount && value <= expensableAmount,
            i18n.t('expenses_v2.forms.errors.taxes.field_exceeds_expensable_amount')
          ),
      })
    ),
  })
}

const TaxSchema = ({ expensableAmount }: FormContext) => {
  return z.object({
    id: z.string(),
    base_amount: z
      .number({
        required_error: i18n.t('expenses_v2.forms.errors.required'),
        invalid_type_error: i18n.t('expenses_v2.forms.errors.invalid_positive_number'),
      })
      .gt(0, defaultInvalidPositiveMessage())
      .refine(
        (value) => expensableAmount && value <= expensableAmount,
        i18n.t('expenses_v2.forms.errors.taxes.field_exceeds_expensable_amount')
      ),
    percentage: z
      .number({
        required_error: i18n.t('expenses_v2.forms.errors.required'),
        invalid_type_error: i18n.t('expenses_v2.forms.errors.invalid_percentage'),
      })
      .min(0, defaultInvalidPercentageMessage())
      .max(100, defaultInvalidPercentageMessage()),
    amount: z
      .number({
        required_error: i18n.t('expenses_v2.forms.errors.required'),
        invalid_type_error: i18n.t('expenses_v2.forms.errors.invalid_number'),
      })
      .gte(0, defaultInvalidNumberMessage())
      .refine(
        (value) => expensableAmount && value <= expensableAmount,
        i18n.t('expenses_v2.forms.errors.taxes.field_exceeds_expensable_amount')
      ),
  })
}

const TaxesSchema = ({ expensableAmount, customizationFields }: FormContext) => {
  return z.object({
    taxes: z.array(z.lazy(() => TaxSchema({ expensableAmount, customizationFields }))),
  })
}

export const superRefineTaxesSchema = ({ data, ctx, customizationFields }: SuperRefineContext) => {
  const { amount } = data
  const isTaxesMandatory = isMandatory('taxes', 'expense', customizationFields)
  const taxesFieldType = fieldType(customizationFields, 'expense', 'taxes') ?? 'tax_type'
  const hasAnyValueTax = data.taxes?.some(
    (tax: Tax) => tax.base_amount != null || tax.percentage != null || tax.amount != null
  )

  if (!isTaxesMandatory && !hasAnyValueTax) {
    return
  }

  if (taxesFieldType === 'tax_type') {
    const taxesValidation = TaxesSchema({
      expensableAmount: amount,
      customizationFields,
    }).safeParse({
      taxes: data.taxes,
    })
    if (!taxesValidation.success) {
      taxesValidation.error.errors.forEach((error) => {
        ctx.addIssue(error)
      })
    }
  } else {
    const singleTaxValidation = SingleTaxSchema({
      expensableAmount: amount,
      customizationFields,
    }).safeParse({
      taxes: data.taxes,
    })

    if (!singleTaxValidation.success) {
      singleTaxValidation.error.errors.forEach((error) => {
        ctx.addIssue(error)
      })
    }
  }
}

export const shouldShowBlock = (
  customizationFields: CustomizationField[],
  resourceType: ExpensesCustomizationConfigResourceTypeEnum,
  documentType: ExpensesValueObjectsCustomizationDocumentTypeEnum
): boolean => {
  return hasAnyEnabledField(['taxes'], resourceType, customizationFields, documentType)
}
